import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { slugify } from '@/features/products';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { contentStatuses } from '../schemas/block-form-schema';
import {
  sectionFormSchema,
  type SectionFormInput,
  type SectionFormOutput,
} from '../schemas/section-form-schema';

import { CustomFieldsEditor } from './custom-fields-editor';

const SECTION_FORM_ID = 'content-section-form';

export interface SectionFormProps {
  mode: 'create' | 'edit';
  defaultValues: SectionFormInput;
  isSubmitting: boolean;
  onSubmit: (values: SectionFormOutput) => Promise<unknown>;
  /** Sections under the form in the main column: the block list. */
  children?: ReactNode;
  aside?: ReactNode;
  dangerZone?: ReactNode;
}

/** Details and the custom bag inside the <form>; status and save in the side rail. The block list is a sibling with its own PUT. */
export function SectionForm({
  mode,
  defaultValues,
  isSubmitting,
  onSubmit,
  children,
  aside,
  dangerZone,
}: SectionFormProps) {
  const { t } = useTranslation(['content', 'common']);
  const message = useErrorMessage();
  const form = useForm<SectionFormInput, unknown, SectionFormOutput>({
    resolver: useI18nResolver(sectionFormSchema),
    defaultValues,
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const [handleTouched, setHandleTouched] = useState(mode === 'edit');
  const isDirty = form.formState.isDirty;
  const rootError = form.formState.errors.root?.server?.message;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await onSubmit(values);
      if (mode === 'edit') form.reset(values);
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  useSaveBar({ form, onSave: submit, saving: isSubmitting });

  return (
    <Form {...form}>
      <FormLayout
        aside={
          <>
            <FormSection title={t('content:sections.status')}>
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="gap-2.5"
                      >
                        {contentStatuses.map((status) => (
                          <div key={status} className="flex items-start gap-2.5">
                            <RadioGroupItem
                              value={status}
                              id={`section-status-${status}`}
                              className="mt-0.5"
                            />
                            <div className="grid gap-0.5">
                              <Label htmlFor={`section-status-${status}`}>
                                {t(`content:status.${status}`)}
                              </Label>
                              <span className="text-muted-foreground text-xs">
                                {t(`content:statusHint.section.${status}`)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Can permission="content.write">
                <div className="flex items-center gap-2 border-t pt-4">
                  <Button
                    type="submit"
                    form={SECTION_FORM_ID}
                    disabled={isSubmitting || (mode === 'edit' && !isDirty)}
                    className="flex-1"
                  >
                    {isSubmitting
                      ? t('common:actions.saving')
                      : mode === 'create'
                        ? t('content:section.create')
                        : t('common:actions.save')}
                  </Button>
                  {mode === 'edit' ? (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={isSubmitting || !isDirty}
                      onClick={() => form.reset()}
                    >
                      {t('content:actions.discard')}
                    </Button>
                  ) : null}
                </div>
              </Can>
            </FormSection>
            {aside}
            {dangerZone}
          </>
        }
      >
        <form id={SECTION_FORM_ID} onSubmit={submit} noValidate className="flex flex-col gap-6">
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          <FormSection
            tour="content.section-editor.details"
            title={t('content:sections.details')}
            description={t('content:sections.sectionDetailsDescription')}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('content:fields.name')}</FormLabel>
                    <FormControl>
                      <Input
                        autoFocus={mode === 'create'}
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          if (!handleTouched)
                            form.setValue('handle', slugify(e.target.value), { shouldDirty: true });
                        }}
                      />
                    </FormControl>
                    <FormDescription>{t('content:fields.nameHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="handle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('content:fields.handle')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="font-mono text-[13px]"
                        onChange={(e) => {
                          setHandleTouched(true);
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormDescription>{t('content:fields.handleHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>{t('content:fields.sectionTitle')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormDescription>{t('content:fields.sectionTitleHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>{t('content:fields.sectionDescription')}</FormLabel>
                    <FormControl>
                      <Textarea rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormSection>
          <FormSection
            title={t('content:sections.custom')}
            description={t('content:sections.customDescription')}
          >
            <CustomFieldsEditor<SectionFormInput>
              name="custom"
              label={t('content:sections.custom')}
            />
          </FormSection>
        </form>
        {children}
      </FormLayout>
    </Form>
  );
}
